import React, { useEffect, useState, useRef } from 'react';
import Select from 'react-select';
import { FaArrowDown } from 'react-icons/fa';
import { FaArrowUp } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';

export const CustomSelectComponent = ({
  handleSelection,
  options,
  resetValue = false,
  selectedOptionDefault,
  isDisabled = false,
  dataTestId = 'customSelect',
  onInputChange = () => { },
  onToggleOpen = () => { },
}) => {
  const { t } = useTranslation(['backoffice']);
  const [inputValue, setInputValue] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const wrapperRef = useRef(null);
  const wasSelectionMadeRef = useRef(false);

  useEffect(() => {
    resetValue && setSelectedOption(null);
  }, [resetValue]);

  useEffect(() => {
    if (selectedOptionDefault && selectedOptionDefault.value && selectedOptionDefault.label) {
      setSelectedOption(selectedOptionDefault);
    } else {
      setSelectedOption(null);
    }
  }, [selectedOptionDefault]);

  useEffect(() => {
    if (isDisabled) setIsSelectOpen(false);
  }, [isDisabled]);

  useEffect(() => {
    if (inputValue && options.length > 0) {
      const filtered = options.filter(
        (option) =>
          option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
          option.subtitle.toLowerCase().includes(inputValue.toLowerCase()),
      );
      setFilteredOptions(filtered);
    } else if (!inputValue) {
      setFilteredOptions([]);
    }
  }, [options, inputValue]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target) && isSelectOpen) {
        if (!wasSelectionMadeRef.current && inputValue) {
          setInputValue('');
          setFilteredOptions([]);
        }
        setIsSelectOpen(false);
        onToggleOpen(false);
        wasSelectionMadeRef.current = false;
      }
    };

    if (isSelectOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSelectOpen, inputValue, onToggleOpen]);

  const handleInputChange = (newValue) => {
    setInputValue(newValue);
    if (newValue.length > 1) {
      onInputChange(newValue);
    }
  };

  const handleSelectChange = (selectedOption) => {
    wasSelectionMadeRef.current = true;
    setSelectedOption(selectedOption);
    if (selectedOption) {
      handleSelection(selectedOption);
    }
    setInputValue('');
    setFilteredOptions([]);
    onInputChange('');
    setIsSelectOpen(false);
    onToggleOpen(false);
  };

  const formatOptionLabel = ({ label, subtitle }) => (
    <div>
      <div className="option-label">{label}</div>
      <div className="optiont-subtitle ">{subtitle}</div>
    </div>
  );

  const handleSelectOpen = () => {
    if (!isDisabled) {
      const next = !isSelectOpen;
      if (isSelectOpen && !next && !wasSelectionMadeRef.current && inputValue) {
        setInputValue('');
        setFilteredOptions([]);
      }
      setIsSelectOpen(next);
      onToggleOpen(next);
      if (next) {
        wasSelectionMadeRef.current = false;
        if (inputValue) {
          setInputValue('');
          setFilteredOptions([]);
        }
      } else {
        wasSelectionMadeRef.current = false;
      }
    }
  };

  const handleMenuClose = () => {
    if (!wasSelectionMadeRef.current && inputValue) {
      setInputValue('');
      setFilteredOptions([]);
    }
    setIsSelectOpen(false);
    onToggleOpen(false);
    wasSelectionMadeRef.current = false;
  };

  return (
    <div className="custom-select-wrapper" ref={wrapperRef}>
      <div
        className={`custom-select-container ${isSelectOpen ? 'open' : ''} ${isDisabled && 'disabled'}`}
        data-testid={dataTestId}
      >
        <div className={`selected-option ${isSelectOpen ? 'select-label' : ''}`} onClick={handleSelectOpen}>
          <div className={`selected-option-content ${isSelectOpen ? 'expanded' : ''}`}>
            <p className="option-label">
              {selectedOption ? selectedOption.label : t('promoCodes.placeholderLibraries')}
            </p>
            {selectedOption && <p className="optiont-subtitle">{selectedOption.subtitle}</p>}
          </div>
          {isSelectOpen ? <FaArrowUp className="arrow-icon" /> : <FaArrowDown className="arrow-icon" />}
        </div>
        {isSelectOpen && (
          <div className="select-dropdown-wrapper">
            <Select
              placeholder={t('search')}
              className="basic-single select-dropdown"
              classNamePrefix="select"
              value={selectedOption}
              inputValue={inputValue}
              isClearable
              isSearchable
              onInputChange={handleInputChange}
              onChange={handleSelectChange}
              onMenuClose={handleMenuClose}
              options={filteredOptions}
              formatOptionLabel={formatOptionLabel}
              menuIsOpen
              isDisabled={isDisabled}
            />
          </div>
        )}
      </div>
    </div>
  );
};
